import { ViewInAr } from '@mui/icons-material';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { EActiveDrawer, ESubMenu, EToolbarButton } from '../../../../@types/Enums';
import ExtendModal from '../modals/ExtendModal';
import ToolbarButton from '../ToolbarButton';

interface ExtendButtonProps {
  sizes: any;
  handleToolbarButtonPress: (buttonId: EToolbarButton, subMenuType?: ESubMenu, activatedDrawer?: EActiveDrawer) => void;
  activeDrawer: EActiveDrawer;
  activeToolbarButton: EToolbarButton | null;
  setActiveToolbarButton: (button: EToolbarButton | null) => void;
  onPositionUpdate: (rect: DOMRect) => void;
}

const ExtendButton = forwardRef<HTMLButtonElement, ExtendButtonProps>(
  ({ sizes, handleToolbarButtonPress, activeDrawer, activeToolbarButton, setActiveToolbarButton, onPositionUpdate }, ref) => {
    const intl = useIntl();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [modalOpen, setModalOpen] = useState(false);
    useImperativeHandle(ref, () => buttonRef.current!);
    
    useEffect(() => {
      if (activeToolbarButton !== EToolbarButton.EXTEND) {
        setModalOpen(false);
      }
    }, [activeToolbarButton]);

    useEffect(() => {
      if (activeToolbarButton === EToolbarButton.EXTEND && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        onPositionUpdate(rect);
      }
    }, [activeToolbarButton, onPositionUpdate]);

    // Parent active state: active if modal is open OR any of its child modes are active.
    const isActive =
      modalOpen ||
      activeDrawer === EActiveDrawer.ROOM_COMPONENTS ||
      activeDrawer === EActiveDrawer.CREATE_ELEMENTS ||
      activeDrawer === EActiveDrawer.TEXTURES;

    const handleClick = () => {
      if (!modalOpen) {
        setModalOpen(true);
        if (activeToolbarButton !== EToolbarButton.EXTEND) {
          setActiveToolbarButton(EToolbarButton.EXTEND);
          handleToolbarButtonPress(EToolbarButton.EXTEND, ESubMenu.NONE, EActiveDrawer.NONE);
        }
      } else {
        setModalOpen(false);
      }
    };

    return (
      <>
        <ToolbarButton
          ref={buttonRef}
          tooltip={intl.formatMessage({ id: 'Toolbar.extend' })}
          onClick={handleClick}
          active={isActive}
          sizes={sizes}
          icon={<ViewInAr />}
        />
        <ExtendModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          sizes={sizes}
          handleToolbarButtonPress={handleToolbarButtonPress}
          activeDrawer={activeDrawer}
          setActiveToolbarButton={setActiveToolbarButton}
          buttonPosition={buttonRef.current?.getBoundingClientRect()}
        />
      </>
    );
  },
);

export default ExtendButton;
